import { EFormControl } from '@common/enums/form-control.enum';
import { IAddressProps } from '@common/general-ui/address/address.component';
import { ICheckboxGroupProps } from '@common/general-ui/checkbox-group/checkbox-group.component';
import { ICheckboxProps } from '@common/general-ui/checkbox/checkbox.component';
import { IInputLoqateProps } from '@common/general-ui/input-loqate/input-loqate.component';
import { IInputProps } from '@common/general-ui/input/input.component';
import { Link } from '@common/general-ui/link/link.component';
import { IMultiSelectProps } from '@common/general-ui/multi-select/multi-select.component';
import { IORSwitchProps } from '@common/general-ui/or-switch/or-switch.component';
import { IRadioGroupProps } from '@common/general-ui/radio-group/radio-group.component';
import { ISelectProps } from '@common/general-ui/select/select.component';
import { ISwitchProps } from '@common/general-ui/switch/switch.component';
import { ITextAreaProps } from '@common/general-ui/textarea/textarea.component';
import { ITimePickerProps } from '@common/general-ui/time-picker/time-picker.component';
import { ITransferProps } from '@common/general-ui/transfer/transfer.component';
import { IUploadImageProps } from '@common/general-ui/upload-image/upload-image.component';
import { RegisterOptionsEx } from '@common/hooks/use-form.hook';
import { t, T } from '@common/hooks/use-t.hook';
import { EField } from '@src/shared/field.enum';
import { LINKS } from '@src/shared/links.constant';
import React from 'react';
import { css } from 'twin.macro';

const styles = {
    link: css`
        color: black;
        font-weight: 300;
        text-decoration: underline;
        font-size: 14px;
    `,
};

type TText = {
    control: EFormControl.text;
} & Omit<IInputProps<string, string>, 'name' | 'onChange'>;
type TTextV2 = {
    control: EFormControl.textV2;
} & Omit<IInputProps<string, string>, 'name' | 'onChange'>;

type TInputLoqate = {
    control: EFormControl.inputLoqate;
} & Omit<IInputLoqateProps<string>, 'name' | 'onChange'>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type TAddress = {
    control: EFormControl.address;
} & Omit<IAddressProps<string>, 'name' | 'onChange'>;

type TPassword = {
    control: EFormControl.password;
} & Omit<IInputProps<string, string>, 'name' | 'onChange'>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type TNumeric = {
    control: EFormControl.numeric;
} & Omit<IInputProps<number, string>, 'name' | 'onChange'>;

type TTextArea = {
    control: EFormControl.textArea;
} & Omit<ITextAreaProps, 'name' | 'onChange'>;

type TSelect = {
    control: EFormControl.select;
} & Omit<ISelectProps, 'name' | 'onChange'>;

type TMultiSelect = {
    control: EFormControl.multiSelect;
} & Omit<IMultiSelectProps<string>, 'name' | 'onChange'>;

type TRadioGroup = {
    control: EFormControl.radioGroup;
} & Omit<IRadioGroupProps<string>, 'name' | 'onChange'>;

type TCheckboxGroup = {
    control: EFormControl.checkboxGroup;
} & Omit<ICheckboxGroupProps<string>, 'name' | 'onChange'>;

type TCheckBox = {
    control: EFormControl.checkbox;
} & Omit<ICheckboxProps<string>, 'name' | 'onChange'>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type TUploadImage = {
    control: EFormControl.uploadImage;
} & Omit<IUploadImageProps<Blob>, 'name' | 'onChange'>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type TOrSwitch = {
    control: EFormControl.orSwitch;
} & Omit<IORSwitchProps<string>, 'name' | 'onChange'>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type TSwitch = {
    control: EFormControl.switch;
} & Omit<ISwitchProps<string>, 'name' | 'onChange'>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type TTimePicker = {
    control: EFormControl.timePicker;
} & Omit<ITimePickerProps<string>, 'name' | 'onChange'>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type TTransfer = {
    control: EFormControl.transfer;
} & Omit<ITransferProps<string>, 'name' | 'onChange'>;

type TEmail = {
    control: EFormControl.email;
} & Omit<IInputProps<string, string>, 'name' | 'onChange'>;

type TPhoneNumber = {
    control: EFormControl.phoneNumberV2;
} & Omit<IInputProps<string, string>, 'name' | 'onChange'>;

interface IProps {
    [EField.registrationCheckPostCode]: TText;
    [EField.registrationCheckPostCodeLoqate]: TInputLoqate;
    [EField.registrationBillingAddress]: TAddress;
    [EField.registrationBoxQuantity]: TSelect;
    [EField.registrationBoxFrequency]: TSelect;
    [EField.email]: TEmail;
    [EField.password]: TTextV2;
    [EField.loginPassword]: TPassword;
    [EField.confirmPassword]: TPassword;
    [EField.registerDiscount]: TText;
    [EField.registerDiscountV2]: TTextV2;

    [EField.subscriptionDeliveryConfig]: TSelect;

    [EField.customerMarketingContent]: TCheckboxGroup;
    [EField.customerWantReceiveNewsletters]: TRadioGroup;
    [EField.customerCommunicationChannels]: TCheckboxGroup;
    [EField.customerId]: TText;
    [EField.customerFirstName]: TTextV2;
    [EField.customerRole]: TSelect;
    [EField.customerLastName]: TTextV2;
    [EField.customerPostCode]: TInputLoqate;
    [EField.customerPhoneNumber]: TPhoneNumber;
    [EField.customerSafePlaceDescription]: TTextArea;
    [EField.registrationCustomerAllergies]: TMultiSelect;
    [EField.customerAllergies]: TMultiSelect;
    [EField.registrationTerms]: TCheckBox;
    [EField.customerWhat3wordsAddress]: TText;

    [EField.sameBillingAddress]: TCheckBox;

    [EField.billingAddress]: TInputLoqate;
}

export const FIELDS: IProps = {
    [EField.registrationCheckPostCode]: {
        control: EFormControl.text,
        type: 'text',
        required: true,
        placeholder: t('Enter your FULL postcode'),
    },
    [EField.registrationCheckPostCodeLoqate]: {
        control: EFormControl.inputLoqate,
        required: true,
        placeholder: t('Start typing your address or postcode'),
    },
    [EField.registrationBillingAddress]: {
        control: EFormControl.address,
        required: true,
        mode: 'allEditable',
        placeholder: t('Start typing your address or postcode'),
    },
    [EField.registrationBoxQuantity]: {
        control: EFormControl.select,

        label: t('Quantity'),
    },
    [EField.registrationBoxFrequency]: {
        control: EFormControl.select,
        label: t('Frequency'),
    },

    [EField.email]: {
        control: EFormControl.email,
        type: 'email',
        label: t('Email address'),
        required: true,
    },
    [EField.password]: {
        control: EFormControl.textV2,
        type: 'password',
        label: t('Password (at least 12 characters)'),
        required: true,
    },
    [EField.loginPassword]: {
        control: EFormControl.password,
        type: 'password',
        label: t('Password'),
        required: true,
    },
    [EField.confirmPassword]: {
        control: EFormControl.password,
        type: 'password',
        label: t('Confirm password'),
        required: true,
    },
    [EField.customerRole]: {
        control: EFormControl.select,
        label: t('User Role'),
        placeholder: t('Please select new user role'),
        required: true,
    },

    [EField.subscriptionDeliveryConfig]: {
        control: EFormControl.select,
        label: t('Delivery route'),
    },
    [EField.registerDiscount]: {
        control: EFormControl.text,
        type: 'text',
        placeholder: t('Enter discount code'),
    },
    [EField.registerDiscountV2]: {
        control: EFormControl.textV2,
        type: 'text',
        placeholder: t('Gift card/discount code'),
    },
    [EField.customerWantReceiveNewsletters]: {
        control: EFormControl.radioGroup,
        label: (
            <span tw="font-normal">
                Would you like to hear about our exclusive offers, tasty recipes, limited-edition add-ons, food waste news
                and the occasional free gift?
            </span>
        ),
        required: true,
    },
    [EField.customerCommunicationChannels]: {
        control: EFormControl.checkboxGroup,
        label: t('How would you like to hear from us?'),
    },
    [EField.customerMarketingContent]: {
        control: EFormControl.checkboxGroup,
        label: t('Which marketing content would you like to receive?'),
    },
    [EField.customerId]: {
        control: EFormControl.text,
        type: 'text',
        label: t('ID'),
        required: true,
    },
    [EField.customerFirstName]: {
        control: EFormControl.textV2,
        type: 'text',
        label: t('First name'),
        required: true,
    },
    [EField.customerLastName]: {
        control: EFormControl.textV2,
        type: 'text',
        label: t('Last name'),
        required: true,
    },

    [EField.customerPostCode]: {
        control: EFormControl.inputLoqate,
        label: t('Delivery address'),
        required: true,
    },
    [EField.customerPhoneNumber]: {
        control: EFormControl.phoneNumberV2,
        type: 'number',
        label: t('Phone number'),
        required: true,
    },
    [EField.customerSafePlaceDescription]: {
        control: EFormControl.textArea,
        required: true,
        label: t('Tell us a safe place to leave your box overnight'),
        placeholder: t('E.g. “in the porch”, “behind the garden gate”, “by the yellow door”'),
        helperText: (
            <span>
                {t(
                    'To keep our emissions low, we deliver overnight, between 7pm (9:30pm for some places) on your delivery day and 7am (8am for some places) the next day. See our',
                )}{' '}
                <Link css={styles.link} target="blank" name="faq" href={LINKS.external_faq}>
                    {t('delivery FAQs')}{' '}
                </Link>
                {t('for more.')}
            </span>
        ),
        validate: {
            validate: (val): string | undefined => {
                return val.replace(/\s/g, '') === ''
                    ? t('We need to know where to leave your box so that it’s safe and sound until the morning.')
                    : undefined;
            },
        },
        getCustomErrorMessage: (opts: RegisterOptionsEx): RegisterOptionsEx => {
            return {
                ...opts,
                required: t('ⓘ We need to know where to leave your box so that it’s safe and sound until the morning.'),
            };
        },
    },
    [EField.registrationCustomerAllergies]: {
        control: EFormControl.multiSelect,
        maxCount: 3,
        label: t('Select up to 3 exclusions. (can be changed weekly)'),
        helperText: (
            <span>
                {t(
                    'We handle celery and celeriac as well as trialling reusable packaging, and our growers may handle all other allergens. For further information. ',
                )}
                <Link css={styles.link} target="blank" name="food-allergies-help" href={LINKS.external_food_allergies}>
                    {t('View allergies/dietary FAQ')}
                </Link>
                .
            </span>
        ),
    },
    [EField.customerAllergies]: {
        control: EFormControl.multiSelect,
        maxCount: 3,
        label: t('Dislikes'),
    },

    [EField.registrationTerms]: {
        control: EFormControl.checkbox,
        required: true,
        label: (
            <T i18nKey="registrationTerms">
                I have read and understood the&nbsp;
                <Link target="blank" name="terms" href={LINKS.external_terms1}>
                    Terms of Business/Privacy Policy and cookies policy
                </Link>
            </T>
        ),
    },
    [EField.customerWhat3wordsAddress]: {
        control: EFormControl.text,
        type: 'text',
        label: t('what3words address'),
        required: false,
        helperText: (
            <>
                {t(
                    'Add your what3words to tell your driver exactly where to leave your box. You can find your what3words on the app or at',
                )}{' '}
                <Link css={styles.link} name={''} target="_blank" href="https://what3words.com/">
                    https://what3words.com/
                </Link>
            </>
        ),
    },

    [EField.sameBillingAddress]: {
        control: EFormControl.checkbox,
        label: t('Same as billing address'),
    },

    [EField.billingAddress]: {
        control: EFormControl.inputLoqate,
        label: t('Billing Address'),
        placeholder: t('Start typing your address or postcode'),
    },
};
